/* Zoomable photo: a scrollable view with zoom out / fit / zoom in buttons, so a teacher can read small print on a worksheet
   while typing beside it. Scale 1 fits the photo to the view's width. Zoom with the buttons, + / - / 0 keys, Ctrl or Cmd + wheel
   (trackpad pinch), a two-finger pinch, or a double click / double tap; drag to pan. Zoom and scroll live in the caller's
   ZoomState, so re-rendering the page around it keeps the teacher's place. The look lives in styles.css (.zoom). */
export interface ZoomState { scale: number; left: number; top: number }

const MIN = 1, MAX = 6, STEP = 1.25, DOUBLE = 2.5;
const clamp = (s: number): number => Math.min(MAX, Math.max(MIN, s));

export const newZoom = (): ZoomState => ({ scale: 1, left: 0, top: 0 });

export function mountZoom(fig: HTMLElement, src: string, alt: string, z: ZoomState = newZoom()): void {
  fig.classList.add("zoom");
  fig.innerHTML = `<div class="zoom-bar" role="toolbar" aria-label="Zoom">
      <button type="button" class="btn quiet" data-z="out" aria-label="Zoom out">−</button>
      <output class="zoom-level" aria-live="polite"></output>
      <button type="button" class="btn quiet" data-z="in" aria-label="Zoom in">+</button>
      <button type="button" class="btn quiet" data-z="fit">Fit</button>
    </div>
    <div class="zoom-view" tabindex="0" aria-label="${alt.replace(/"/g, "&quot;")}. Use plus and minus to zoom."><img draggable="false"></div>`;
  const view = fig.querySelector<HTMLElement>(".zoom-view")!, img = view.querySelector("img")!;
  const level = fig.querySelector("output")!;
  const btn = (k: string): HTMLButtonElement => fig.querySelector<HTMLButtonElement>(`[data-z="${k}"]`)!;
  img.alt = alt; img.src = src;

  const paint = (): void => {
    img.style.width = `${z.scale * 100}%`;
    level.textContent = `${Math.round(z.scale * 100)}%`;
    btn("out").disabled = z.scale <= MIN; btn("fit").disabled = z.scale <= MIN; btn("in").disabled = z.scale >= MAX;
    view.classList.toggle("zoomed", z.scale > MIN);
  };
  const remember = (): void => { z.left = view.scrollLeft; z.top = view.scrollTop; };
  // zoom to `to`, keeping the photo point under (x, y) (view coordinates; default the centre) in place
  const zoomTo = (to: number, x = view.clientWidth / 2, y = view.clientHeight / 2): void => {
    const s = clamp(to); if (s === z.scale) return;
    const k = s / z.scale, left = (view.scrollLeft + x) * k - x, top = (view.scrollTop + y) * k - y;
    z.scale = s; paint(); view.scrollLeft = left; view.scrollTop = top; remember();
  };
  const at = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = view.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top];
  };

  paint();
  const restore = (): void => { view.scrollLeft = z.left; view.scrollTop = z.top; };
  if (img.complete) restore(); else img.addEventListener("load", restore, { once: true });

  btn("in").onclick = () => zoomTo(z.scale * STEP);
  btn("out").onclick = () => zoomTo(z.scale / STEP);
  btn("fit").onclick = () => zoomTo(MIN);
  view.addEventListener("scroll", remember, { passive: true });
  view.addEventListener("keydown", e => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === "+" || e.key === "=") zoomTo(z.scale * STEP);
    else if (e.key === "-" || e.key === "_") zoomTo(z.scale / STEP);
    else if (e.key === "0") zoomTo(MIN);
    else return;
    e.preventDefault();
  });
  // plain wheel scrolls as usual; Ctrl/Cmd + wheel (and trackpad pinch, which arrives as one) zooms
  view.addEventListener("wheel", e => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault(); zoomTo(z.scale * Math.exp(-e.deltaY / 200), ...at(e));
  }, { passive: false });
  // touch double taps are handled below, so a browser that also sends dblclick for them doesn't zoom twice
  let lastType = "mouse";
  view.addEventListener("dblclick", e => { if (lastType === "touch") return; e.preventDefault(); zoomTo(z.scale > MIN ? MIN : DOUBLE, ...at(e)); });

  // drag to pan, two fingers to pinch. At fit, a one-finger swipe is left to the browser so the page still scrolls
  // (styles.css only turns off native touch panning on .zoomed).
  const pts = new Map<number, [number, number]>();
  let pinch: { dist: number; scale: number } | null = null, lastTap = 0;
  const spread = (): number => { const [a, b] = [...pts.values()]; return Math.hypot(a[0] - b[0], a[1] - b[1]); };
  const mid = (): [number, number] => { const [a, b] = [...pts.values()]; return at({ clientX: (a[0] + b[0]) / 2, clientY: (a[1] + b[1]) / 2 }); };
  view.addEventListener("pointerdown", e => {
    lastType = e.pointerType;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (e.pointerType === "touch" && pts.size === 0) {
      const now = e.timeStamp; if (now - lastTap < 300) { zoomTo(z.scale > MIN ? MIN : DOUBLE, ...at(e)); lastTap = 0; return; } lastTap = now;
    }
    pts.set(e.pointerId, [e.clientX, e.clientY]); view.setPointerCapture(e.pointerId);
    if (pts.size === 2) pinch = { dist: spread(), scale: z.scale };
  });
  view.addEventListener("pointermove", e => {
    const prev = pts.get(e.pointerId); if (!prev) return;
    pts.set(e.pointerId, [e.clientX, e.clientY]);
    if (pinch && pts.size === 2) { const d = spread(); if (pinch.dist > 0) zoomTo(pinch.scale * d / pinch.dist, ...mid()); return; }
    if (pts.size === 1 && (e.pointerType !== "touch" || z.scale > MIN)) { view.scrollLeft -= e.clientX - prev[0]; view.scrollTop -= e.clientY - prev[1]; view.classList.add("panning"); }
  });
  const up = (e: PointerEvent): void => {
    pts.delete(e.pointerId); if (pts.size < 2) pinch = null;
    if (!pts.size) view.classList.remove("panning");
  };
  view.addEventListener("pointerup", up); view.addEventListener("pointercancel", up);
}
